import { useEffect, useRef, useState, type ReactElement } from 'react';

import { ApiError } from '../lib/api';
import { useAuth } from '../store/auth';

/**
 * The screen the confirmation link opens.
 *
 * It runs without a session on purpose: the link is usually followed from a
 * mail client, sometimes on another device, and the token in the URL is the
 * whole credential. So the token is spent as soon as the page mounts, and what
 * is shown afterwards is either "done" or the sentence the API sent back —
 * expired and already-used call for different next steps, and the API tells
 * them apart.
 */

const TOKEN_QUERY_PARAM = 'token';
const MISSING_TOKEN = 'O link de confirmação está incompleto. Abra-o direto do e-mail.';
const GENERIC_FAILURE = 'Não foi possível confirmar o e-mail. Tente de novo.';

type Status = 'verifying' | 'confirmed' | 'failed';

function readTokenFromLocation(): string {
  return new URLSearchParams(window.location.search).get(TOKEN_QUERY_PARAM) ?? '';
}

export function VerifyEmailPage(): ReactElement {
  const { verifyEmail } = useAuth();
  const [status, setStatus] = useState<Status>('verifying');
  const [error, setError] = useState<string | null>(null);

  /**
   * The token is single-use, so spending it twice would turn a good link into
   * "already used". React's StrictMode runs effects twice in development, which
   * is exactly that double spend — the guard makes the send happen once.
   */
  const started = useRef(false);

  useEffect(() => {
    if (started.current) {
      return;
    }
    started.current = true;

    const token = readTokenFromLocation();
    if (token === '') {
      setStatus('failed');
      setError(MISSING_TOKEN);
      return;
    }

    void verifyEmail(token)
      .then(() => {
        setStatus('confirmed');
      })
      .catch((cause: unknown) => {
        setStatus('failed');
        setError(cause instanceof ApiError ? cause.message : GENERIC_FAILURE);
      });
  }, [verifyEmail]);

  return (
    <main className="auth-screen">
      <div className="auth-card">
        <h1 className="auth-title">Discordo</h1>

        {status === 'verifying' && <p className="auth-subtitle">Confirmando seu e-mail…</p>}

        {status === 'confirmed' && (
          <>
            <p className="auth-subtitle">E-mail confirmado!</p>
            <p className="auth-footnote">
              Já pode conversar. <a href="/">Abrir o Discordo</a>
            </p>
          </>
        )}

        {status === 'failed' && (
          <>
            <p className="auth-alert" role="alert">
              {error}
            </p>
            <p className="auth-footnote">
              Entre na sua conta para pedir outro link. <a href="/login">Entrar</a>
            </p>
          </>
        )}
      </div>
    </main>
  );
}

export default VerifyEmailPage;
